/** The KPI-driven P5 tabs: Liquidity, Credit & reserves, Capital & marks (reported → marked CET1), the NII bridge and Peers. */
import type { ContentParams, KpiId, KpiSnapshot, PcaStatus } from "@bsim/domain";
import type { RoundReport } from "@bsim/contracts";
import { formatBp, formatKpi, formatKpiChange, formatMillionsBare, formatUsdM, rateDeltaBp } from "../format";
import { kpiLabel, labelOf } from "../format/labels";
import type { TableRow } from "./DataTable";
import type { Statements } from "./balance";

export const LIQUIDITY_KPIS: readonly KpiId[] = ["lcrLite", "slc", "ldr", "adjLdr", "uninsuredShare", "wholesaleShare", "depositGrowth", "costOfDeposits"];
export const CREDIT_KPIS: readonly KpiId[] = ["ncoRate", "nplRate", "aclRatio", "provision", "creToTrbc", "constructionToTrbc", "creGrowth36m", "loanGrowth"];

/** KPIs whose figure is the period's last month, not the period's total (R10): said in the row so it is not read against the statement's period figure. */
const MONTH_FIGURES: ReadonlySet<KpiId> = new Set<KpiId>(["provision"]);

/**
 * One row per KPI present in the period: value, prior quarter, change and (optionally) the difference from the status-quo twin.
 * `twin` = `Attribution.vsAutopilot`, actual − twin in the KPI's unit.
 */
export function kpiRows(ids: readonly KpiId[], cur: KpiSnapshot, prior: KpiSnapshot | null, twin: KpiSnapshot | null): TableRow[] {
  return ids.flatMap((id) => {
    const now = cur[id];
    if (now === undefined || (prior === null && now === 0 && MONTH_FIGURES.has(id))) return [];
    const before = prior?.[id];
    const vs = twin?.[id];
    return [{
      id, label: `${kpiLabel(id)}${MONTH_FIGURES.has(id) ? " (last month)" : ""}`, term: id,
      // A column with nothing behind it is not drawn: no prior period, no prior or change.
      cells: [formatKpi(id, now), ...(prior === null ? [] : [before === undefined ? null : formatKpi(id, before), before === undefined ? null : formatKpiChange(id, now - before)]), ...(twin === null ? [] : [vs === undefined ? null : formatKpiChange(id, vs)])],
    }];
  });
}

// A column with nothing behind it is not drawn: with no prior period a row is its figure alone.
const usdRow = (id: string, label: string, cur: number | null, prior: number | null, hasPrior: boolean, signed = true): TableRow => ({
  id, label, cells: [cur === null ? null : formatUsdM(cur), ...(hasPrior ? [prior === null ? null : formatUsdM(prior), cur === null || prior === null ? null : formatUsdM(cur - prior, { signed })] : [])],
});

/** ACL balance and provision come from the statements, beside the credit KPIs. The provision row waits for a period that has earned something. */
export const reserveRows = (cur: Statements, prior: Statements | null): TableRow[] => [
  usdRow("aclUsd", "Allowance for credit losses", cur.balanceSheet.aclUsd, prior?.balanceSheet.aclUsd ?? null, prior !== null),
  ...(prior === null && cur.incomeStatement.provisionUsd === 0 ? [] : [usdRow("provisionUsd", "Provision (statement, for the period)", cur.incomeStatement.provisionUsd, prior?.incomeStatement.provisionUsd ?? null, prior !== null)]),
];

/** NII bridge (one month's figures, so a decimal: the steps are small), in order: prior → volume → rate index → spread → beta lag → mix → current. */
export function niiBridgeRows(bridge: RoundReport["attribution"]["niiBridge"]): TableRow[] {
  const steps: readonly (readonly [string, string, number])[] = [
    ["volume", "Volume", bridge.volumeUsd], ["rateIndex", "Rate index", bridge.rateIndexUsd], ["spread", "Spread", bridge.spreadUsd],
    ["betaLag", "Deposit beta lag", bridge.betaLagUsd], ["mix", "Mix", bridge.mixUsd],
  ];
  return [
    { id: "prior", label: "Prior net interest income", cells: [formatMillionsBare(bridge.priorUsd, { precision: 1 })] },
    ...steps.map(([id, label, v]): TableRow => ({ id, label, cells: [formatMillionsBare(v, { signed: true, precision: 1 })] })),
    { id: "current", label: "Net interest income", total: true, cells: [formatMillionsBare(bridge.currentUsd, { precision: 1 })] },
  ];
}

/** True when the steps add up to the change within $0.1M (P5 acceptance 2). */
export function niiBridgeSums(bridge: RoundReport["attribution"]["niiBridge"]): boolean {
  const steps = bridge.volumeUsd + bridge.rateIndexUsd + bridge.spreadUsd + bridge.betaLagUsd + bridge.mixUsd;
  return Math.abs(bridge.priorUsd + steps - bridge.currentUsd) <= 100_000;
}

/**
 * Reported → marked CET1. The AFS step is the AOCI in equity over risk-weighted assets; the HTM step is what is left of the gap, so
 * the rows always reconcile. Without `rwa` in the period's KPIs only the totals are printed and the steps read "—".
 */
export function capitalRows(kpis: KpiSnapshot, s: Statements): TableRow[] {
  const reported = kpis.cet1Reported;
  const marked = kpis.cet1Marked;
  if (reported === undefined || marked === undefined) return [];
  const rwa = kpis.rwa;
  const afsBp = rwa === undefined ? null : Math.round((s.balanceSheet.equity.aociAfsUsd / rwa) * 10_000);
  const totalBp = rateDeltaBp(marked, reported);
  const htmBp = afsBp === null ? null : totalBp - afsBp;
  const bp = (v: number | null) => (v === null ? null : formatBp(v, { signed: true }));
  return [
    { id: "cet1Reported", label: labelOf("cet1Reported"), term: "cet1Reported", cells: [formatKpi("cet1Reported", reported)] },
    { id: "afsMarks", label: "Bond marks in equity (AFS, after tax)", cells: [bp(afsBp)] },
    { id: "htmMark", label: "Held-to-maturity mark (after tax)", cells: [bp(htmBp)] },
    { id: "cet1Marked", label: labelOf("cet1Marked"), term: "cet1Marked", total: true, cells: [formatKpi("cet1Marked", marked)] },
    { id: "gap", label: "Gap · reported less marked", cells: [formatBp(-totalBp)] },
  ];
}

/** The regulator's capital category in words (the prompt-corrective-action ladder). */
export const PCA_WORDS: Readonly<Record<PcaStatus, string>> = {
  wellCapitalized: "Well capitalized", adequatelyCapitalized: "Adequately capitalized", undercapitalized: "Undercapitalized",
  significantlyUndercapitalized: "Significantly undercapitalized", criticallyUndercapitalized: "Critically undercapitalized",
};

/**
 * Where the bank's CET1 sits against the regulator's lines (F16.5 E, R9): the regulatory minimum (4.5%), the well-capitalized line (6.5%), the minimum plus the conservation buffer (7.0%),
 * the bank's headroom over the minimum and over the minimum plus buffer, and the capital category the regulator puts it in. The lines are the content parameters', the category and the buffer held
 * the report's (`scorecard.pca`, `.ccb`); a row whose source is absent is left out, never read as zero. The management rating's own line (own target less 50bp) is the CRO's, not a regulator line.
 */
export function regulatoryLineRows(kpis: KpiSnapshot, capital: ContentParams["capital"] | undefined, scorecard: Pick<RoundReport["scorecard"], "pca" | "ccb"> | undefined): TableRow[] {
  const reported = kpis.cet1Reported;
  if (capital === undefined || reported === undefined) return [];
  const minimum = capital.minimums.cet1Rate;
  const buffer = capital.ccbTiers.reduce((top, t) => Math.max(top, t.bufferAtMostRate), 0);
  const pct = (v: number): string => formatKpi("cet1Reported", v);
  const headroom = (line: number): string => formatBp(rateDeltaBp(reported, line), { signed: true });
  return [
    { id: "regMinimum", label: "Regulatory minimum", cells: [pct(minimum)] },
    { id: "regWellCapitalized", label: "Well-capitalized line", cells: [pct(capital.wellCapitalized.cet1Rate)] },
    ...(buffer === 0 ? [] : [{ id: "regBufferLine", label: "Minimum plus conservation buffer", cells: [pct(minimum + buffer)] }]),
    { id: "regHeadroomMinimum", label: "Your headroom over the minimum", cells: [headroom(minimum)] },
    ...(buffer === 0 ? [] : [{ id: "regHeadroomBuffer", label: "Your headroom over minimum plus buffer", cells: [headroom(minimum + buffer)] }]),
    ...(scorecard === undefined ? [] : [
      { id: "regBufferHeld", label: "Conservation buffer you hold", cells: [pct(scorecard.ccb.bufferRate)] },
      { id: "regCategory", label: "Your capital category with the regulator", cells: [PCA_WORDS[scorecard.pca]] },
    ]),
  ];
}
